import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateMemberRoleDefinitionOptions,
  CreateMemberRoleDefinitionResponse,
  DeleteMemberRoleDefinitionApplicationErrors,
  GetMemberRoleDefinitionResponse,
  ListMemberRoleDefinitionsResponse,
  UpdateMemberRoleDefinitionApplicationErrors,
  UpdateMemberRoleDefinitionOptions,
  UpdateMemberRoleDefinitionResponse,
  createMemberRoleDefinition as universalCreateMemberRoleDefinition,
  deleteMemberRoleDefinition as universalDeleteMemberRoleDefinition,
  getMemberRoleDefinition as universalGetMemberRoleDefinition,
  listMemberRoleDefinitions as universalListMemberRoleDefinitions,
  updateMemberRoleDefinition as universalUpdateMemberRoleDefinition,
} from './members-v1-member-role-definition-member-role-definition.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function createMemberRoleDefinition(
  httpClient: HttpClient
): CreateMemberRoleDefinitionSignature {
  return (
    roleKey: string,
    options?: NonNullablePaths<
      CreateMemberRoleDefinitionOptions,
      `roleTitle`,
      2
    >
  ) =>
    universalCreateMemberRoleDefinition(
      roleKey,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateMemberRoleDefinitionSignature {
  /**
   * Creates a new role definition.
   *
   * A role must be defined before it can be assigned to members using
   * [Assign Role](https://dev.wix.com/docs/rest/crm/members-contacts/members/role-v1/assign-role).
   * The `roleKey` must be unique across the site.
   * @param - Unique key that identifies the role. Must be uppercase (e.g. `"WRITER"`).
   */
  (
    roleKey: string,
    options?: NonNullablePaths<
      CreateMemberRoleDefinitionOptions,
      `roleTitle`,
      2
    >
  ): Promise<
    NonNullablePaths<
      CreateMemberRoleDefinitionResponse,
      `role._id` | `role.roleKey` | `role.title` | `role.blockable`,
      3
    >
  >;
}

export function listMemberRoleDefinitions(
  httpClient: HttpClient
): ListMemberRoleDefinitionsSignature {
  return () =>
    universalListMemberRoleDefinitions(
      // @ts-ignore
      { httpClient }
    );
}

interface ListMemberRoleDefinitionsSignature {
  /**
   * Lists all role definitions for the site.
   *
   * Returns both system-defined roles and custom roles created via
   * [Create Member Role Definition](https://dev.wix.com/docs/rest/crm/members-contacts/members/member-role-definition-v1/create-member-role-definition).
   */
  (): Promise<
    NonNullablePaths<
      ListMemberRoleDefinitionsResponse,
      | `roles`
      | `roles.${number}._id`
      | `roles.${number}.roleKey`
      | `roles.${number}.title`
      | `roles.${number}.blockable`,
      4
    >
  >;
}

export function getMemberRoleDefinition(
  httpClient: HttpClient
): GetMemberRoleDefinitionSignature {
  return (roleKey: string) =>
    universalGetMemberRoleDefinition(
      roleKey,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMemberRoleDefinitionSignature {
  /**
   * Retrieves a role definition by its role key.
   *
   * To retrieve all role definitions for the site, use
   * [List Member Role Definitions](https://dev.wix.com/docs/rest/crm/members-contacts/members/member-role-definition-v1/list-member-role-definitions).
   * @param - Key of the role definition to retrieve.
   */
  (roleKey: string): Promise<
    NonNullablePaths<
      GetMemberRoleDefinitionResponse,
      `role._id` | `role.roleKey` | `role.title` | `role.blockable`,
      3
    >
  >;
}

export function updateMemberRoleDefinition(
  httpClient: HttpClient
): UpdateMemberRoleDefinitionSignature {
  return (
    key: string,
    options?: NonNullablePaths<UpdateMemberRoleDefinitionOptions, `revision`, 2>
  ) =>
    universalUpdateMemberRoleDefinition(
      key,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMemberRoleDefinitionSignature {
  /**
   * Updates a role definition.
   *
   * Only the `blockable` field can be updated.
   * Pass the current `revision` value to prevent conflicting concurrent updates.
   * The `revision` is incremented on each successful update.
   * System roles cannot be updated.
   * @param - Key of the role definition to update.
   */
  (
    key: string,
    options?: NonNullablePaths<UpdateMemberRoleDefinitionOptions, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateMemberRoleDefinitionResponse,
      `role._id` | `role.roleKey` | `role.title` | `role.blockable`,
      3
    > & {
      __applicationErrorsType?: UpdateMemberRoleDefinitionApplicationErrors;
    }
  >;
}

export function deleteMemberRoleDefinition(
  httpClient: HttpClient
): DeleteMemberRoleDefinitionSignature {
  return (roleKey: string) =>
    universalDeleteMemberRoleDefinition(
      roleKey,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMemberRoleDefinitionSignature {
  /**
   * Deletes a role definition.
   *
   * Once deleted, the role can no longer be assigned to members.
   * Members who currently have the role retain it until it is explicitly unassigned.
   * System roles cannot be deleted.
   * @param - Key of the role definition to delete.
   */
  (roleKey: string): Promise<
    void & {
      __applicationErrorsType?: DeleteMemberRoleDefinitionApplicationErrors;
    }
  >;
}

export {
  Asset,
  CreateMemberRoleDefinitionOptions,
  CreateMemberRoleDefinitionRequest,
  CreateMemberRoleDefinitionResponse,
  DeleteContext,
  DeleteMemberRoleDefinitionRequest,
  DeleteMemberRoleDefinitionResponse,
  DeleteStatus,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  GetMemberRoleDefinitionRequest,
  GetMemberRoleDefinitionResponse,
  ListMemberRoleDefinitionsRequest,
  ListMemberRoleDefinitionsResponse,
  MemberRoleDefinition,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  SyncMetaSiteRolesRequest,
  SyncMetaSiteRolesResponse,
  UpdateMemberRoleDefinitionOptions,
  UpdateMemberRoleDefinitionRequest,
  UpdateMemberRoleDefinitionResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WixelAssigned,
  WixelUnassigned,
} from './members-v1-member-role-definition-member-role-definition.universal.js';
